import { UserPlusIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@heroui/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type FC, useMemo, useState } from 'react';
import { useDebounce } from '@/app/hooks.ts';
import toast from '@/app/toast.ts';
import { useAttachAccountRepresentativeMutation } from '@/slices/account-representative-api-slice.ts';
import { useFetchSearchRepresentativesQuery } from '@/slices/representative-api-slice.ts';

interface ConnectRepDialogProps {
  accountKey: string;
  accountName: string;
  isOpen: boolean;
  onClose: () => void;
}

const ConnectRepDialog: FC<ConnectRepDialogProps> = ({
  accountKey,
  accountName,
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRepKey, setSelectedRepKey] = useState<string | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 350);
  const trimmedDebouncedSearch = debouncedSearchQuery.trim();
  const searchParams = trimmedDebouncedSearch ? { keyword: trimmedDebouncedSearch } : {};
  const isSearchPending = searchQuery.trim() !== trimmedDebouncedSearch;
  const { data: reps, isFetching } = useFetchSearchRepresentativesQuery(searchParams, {
    skip: !isOpen,
  });
  const [attach, { isLoading: isAttaching }] = useAttachAccountRepresentativeMutation();

  const filteredReps = useMemo(() => reps ?? [], [reps]);

  const getConnectErrorMessage = (error: unknown) => {
    if (typeof error === 'object' && error !== null && 'data' in error) {
      const data = (error as FetchBaseQueryError).data;
      if (typeof data === 'string' && data.trim()) {
        return data;
      }
      if (typeof data === 'object' && data !== null && 'message' in data) {
        const message = (data as { message?: unknown }).message;
        if (typeof message === 'string' && message.trim()) {
          return message;
        }
      }
    }

    return 'Failed to connect representative.';
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedRepKey(null);
    onClose();
  };

  const handleConnect = async () => {
    if (!selectedRepKey) {
      return;
    }

    try {
      await attach({ accountKey, representativeKey: selectedRepKey }).unwrap();
      toast.success('Representative connected to account.');
      handleClose();
    } catch (error) {
      toast.error(getConnectErrorMessage(error));
    }
  };

  const showFetchingOverlay = isFetching && !isSearchPending;
  const showEmptyState = !showFetchingOverlay && !isSearchPending && filteredReps.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size='lg'
      placement='center'
      backdrop='blur'
      classNames={{
        base: 'overflow-hidden border border-zinc-200 shadow-2xl dark:border-zinc-700',
        body: 'min-h-0 flex-1 overflow-hidden',
        footer: 'shrink-0',
      }}
    >
      <ModalContent className='flex h-[42rem] max-h-[85vh] flex-col overflow-hidden'>
        <ModalHeader className='flex shrink-0 items-start gap-3 border-b border-primary-100 bg-gradient-to-br from-primary-50 via-white to-secondary-50 px-6 py-5 dark:border-zinc-700 dark:from-primary-950/60 dark:via-zinc-900 dark:to-secondary-950/30'>
          <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-900/50 dark:text-primary-300 dark:ring-primary-800'>
            <UserPlusIcon className='size-5' />
          </span>
          <span>
            <span className='block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-secondary-600 dark:text-secondary-400'>
              Admin action
            </span>
            <span className='mt-1 block font-serif text-lg font-semibold text-zinc-950 dark:text-white'>
              Connect Representative
            </span>
          </span>
        </ModalHeader>
        <ModalBody className='flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-6 py-5'>
          <p className='shrink-0 text-sm text-zinc-600 dark:text-zinc-300'>
            Connect an existing representative to{' '}
            <span className='font-medium text-zinc-900 dark:text-white'>{accountName}</span>. They
            will be approved immediately and assigned the account&apos;s default rep category.
          </p>
          <Input
            label='Search representatives'
            placeholder='Name, email, company...'
            value={searchQuery}
            onValueChange={setSearchQuery}
            isClearable
            className='shrink-0'
          />
          <div className='relative min-h-0 flex-1 overflow-y-auto rounded-xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-950/40'>
            {showFetchingOverlay && (
              <div className='absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-zinc-950/70'>
                <Spinner size='sm' color='primary' />
              </div>
            )}
            {showEmptyState && (
              <div className='flex h-full items-center justify-center px-4 text-sm text-zinc-500'>
                No representatives found.
              </div>
            )}
            {!showEmptyState && (
              <div className='space-y-1 p-2'>
                {filteredReps.map(repResult => {
                  const rep = repResult.representative;
                  const isSelected = selectedRepKey === rep.key;
                  return (
                    <button
                      key={rep.key}
                      type='button'
                      className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary-50 dark:bg-primary-950/30'
                          : 'border-transparent hover:border-zinc-200 hover:bg-zinc-50 dark:hover:border-zinc-700 dark:hover:bg-zinc-900'
                      }`}
                      onClick={() => setSelectedRepKey(rep.key)}
                    >
                      <div className='font-medium text-zinc-900 dark:text-white'>
                        {rep.firstName} {rep.lastName}
                      </div>
                      <div className='text-xs text-zinc-500'>{rep.emailAddress}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className='shrink-0 border-t border-zinc-100 px-6 py-4 dark:border-zinc-800'>
          <Button variant='flat' onPress={handleClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            isDisabled={!selectedRepKey}
            isLoading={isAttaching}
            onPress={handleConnect}
          >
            Connect Representative
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConnectRepDialog;
